import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ScheduleSubmissionsService } from 'src/app/services/api/schedule-submissions/schedule-submissions.service';
import { TeachCompensationService } from 'src/app/services/api/teach-compensation/teach-compensation.service';
import {
  ProjectResearchSubjectBlockModel,
  ScheduleSubmissionBlockModel,
  ScheduleSubmissionsTableModel,
  SubjectScheduleBlockModel,
} from 'src/app/services/models/schedule/schedule-submissions.model';
import { MakeupTeachingRequestModel } from 'src/app/services/models/teach-compensation/teach-compensation.model';
import { SharedModule } from 'src/shared/shared.module';

interface PreviewField {
  label: string;
  value: string;
  required?: boolean;
  columnClass?: string;
  multiline?: boolean;
}

interface TeacherNameSource {
  full_name: string;
  academic_rank?: string | null;
}

interface TimetableHour {
  label: string;
  colSpan: number;
}

interface TimetableDay {
  dayOfWeek: number;
  thaiName: string;
  englishName: string;
}

interface TimetableEvent {
  startSlot: number;
  endSlot: number;
  subjectLabel: string;
  subjectName: string;
  detailLabel: string;
}

interface TimetableCell {
  colSpan: number;
  events: TimetableEvent[];
}

interface TimetableRow extends TimetableDay {
  cells: TimetableCell[];
}

const TIMETABLE_START_HOUR = 7;
const TIMETABLE_END_HOUR = 21;
const TIMETABLE_SLOT_MINUTES = 30;
const TIMETABLE_SLOTS_PER_HOUR = 60 / TIMETABLE_SLOT_MINUTES;
const TIMETABLE_TOTAL_SLOTS = ((TIMETABLE_END_HOUR - TIMETABLE_START_HOUR) * 60) / TIMETABLE_SLOT_MINUTES;

const TIMETABLE_HOURS: TimetableHour[] = Array.from(
  { length: TIMETABLE_END_HOUR - TIMETABLE_START_HOUR },
  (_, index) => {
    const hour = TIMETABLE_START_HOUR + index;

    return {
      label: `${hour}:00-${hour + 1}:00`,
      colSpan: TIMETABLE_SLOTS_PER_HOUR,
    };
  }
);

const TIMETABLE_DAYS: TimetableDay[] = [
  { dayOfWeek: 1, thaiName: 'วันจันทร์', englishName: 'Monday' },
  { dayOfWeek: 2, thaiName: 'วันอังคาร', englishName: 'Tuesday' },
  { dayOfWeek: 3, thaiName: 'วันพุธ', englishName: 'Wednesday' },
  { dayOfWeek: 4, thaiName: 'วันพฤหัสบดี', englishName: 'Thursday' },
  { dayOfWeek: 5, thaiName: 'วันศุกร์', englishName: 'Friday' },
  { dayOfWeek: 6, thaiName: 'วันเสาร์', englishName: 'Saturday' },
  { dayOfWeek: 7, thaiName: 'วันอาทิตย์', englishName: 'Sunday' },
];

@Component({
  selector: 'app-teach-compensation-preview',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    TranslateModule,
  ],
  templateUrl: './teach-compensation-preview.component.html',
  styleUrls: [
    '../teach-compensation-details/teach-compensation-details.component.scss',
    './teach-compensation-preview.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class TeachCompensationPreviewComponent implements OnInit {
  readonly timetableHours = TIMETABLE_HOURS;
  readonly timetableRows = signal<TimetableRow[]>(this.buildTimetableRows([]));
  readonly previewRows = signal<PreviewField[][]>([]);
  readonly teacherName = signal('-');
  readonly departmentName = signal('-');
  readonly academicTermName = signal('');
  readonly academicYear = signal('');
  readonly hasAcademicTermPreview = computed(
    () => this.academicTermName() !== '' && this.academicYear() !== '',
  );
  readonly loading = signal(false);

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly teachCompensationService = inject(TeachCompensationService);
  private readonly scheduleSubmissionService = inject(ScheduleSubmissionsService);

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    if (!Number.isFinite(id) || id <= 0) {
      void this.router.navigate(['/teach-compensation-management/teach-compensation']);
      return;
    }

    this.loadRequest(id);
  }

  private loadRequest(id: number): void {
    this.loading.set(true);
    this.teachCompensationService.getMakeupTeachingRequest(id).subscribe({
      next: (request) => {
        this.loading.set(false);
        this.previewRows.set(this.buildPreviewRows(request));
        this.loadScheduleSubmissionTable(request.schedule_submission_id);
      },
      error: () => {
        this.loading.set(false);
        void this.router.navigate(['/teach-compensation-management/teach-compensation']);
      },
    });
  }

  private loadScheduleSubmissionTable(scheduleSubmissionId?: number | null): void {
    if (scheduleSubmissionId == null) {
      return;
    }

    this.scheduleSubmissionService.getScheduleSubmissionTabel(scheduleSubmissionId).subscribe({
      next: (res) => {
        const department = res.teacher.department;

        this.academicTermName.set(String(res.academic_term.name ?? ''));
        this.academicYear.set(String(this.toBuddhistYear(res.academic_term.year)));
        this.teacherName.set(this.formatTeacherName(res.teacher));
        this.departmentName.set(
          department.name_th ||
          department.name ||
          department.name_en ||
          '-'
        );
        this.timetableRows.set(this.buildTimetableRows(res.blocks));
      },
      error: () => {
        this.timetableRows.set(this.buildTimetableRows([]));
      },
    });
  }

  private formatTeacherName(teacher: TeacherNameSource | null | undefined): string {
    const fullName = teacher?.full_name.trim() ?? '';
    const academicRank = String(teacher?.academic_rank ?? '').trim();

    if (fullName === '') {
      return '-';
    }

    return academicRank === '' ? fullName : `${academicRank} ${fullName}`;
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private buildPreviewRows(request: MakeupTeachingRequestModel): PreviewField[][] {
    return [
      [
        { label: 'ประเภทการลา / หยุด', value: this.displayValue(request.makeup_teaching_leave_type?.name), required: true },
        { label: this.getLeaveDateLabel(request), value: this.formatLeaveDate(request), required: true },
        { label: 'ช่วงเวลา', value: this.formatLeavePeriod(request), required: true },
      ],
      [
        {
          label: 'ชื่อรายวิชา',
          value: this.getSubjectName(request),
          required: true,
          columnClass: 'col-sm-8',
        },
        {
          label: 'รหัสวิชา',
          value: this.getSubjectCode(request),
          required: true,
          columnClass: 'col-sm-4',
        },
      ],
      [
        { label: 'วันที่ขอสอนชดเชย', value: this.formatDate(request.makeup_teaching_date), required: true },
        { label: 'เวลาสอนชดเชย', value: this.formatTimeRange(
          request.makeup_start_time?.label ?? request.makeup_start_time?.time_value,
          request.makeup_end_time?.label ?? request.makeup_end_time?.time_value,
        ), required: true },
      ],
      [
        {
          label: 'คำอธิบาย',
          value: this.displayValue(request.description),
          columnClass: 'col-12',
          multiline: true,
        },
      ],
    ];
  }

  private getLeaveDateLabel(request: MakeupTeachingRequestModel): string {
    return request.academic_holiday != null ? 'วันที่หยุด' : 'วันที่ลา';
  }

  private formatLeaveDate(request: MakeupTeachingRequestModel): string {
    const holidayName = request.academic_holiday?.name;
    const holidayDate = this.formatDate(request.academic_holiday?.holiday_date);

    if (holidayName != null && holidayName !== '' && holidayDate !== '-') {
      return `${holidayName} (${holidayDate})`;
    }

    if (holidayDate !== '-') {
      return holidayDate;
    }

    return this.formatDate(request.leave_date);
  }

  private formatLeavePeriod(request: MakeupTeachingRequestModel): string {
    const period = request.makeup_teaching_leave_period;
    const periodName = period?.name ?? period?.label;
    const startTime = this.formatTime(period?.start_time);
    const endTime = this.formatTime(period?.end_time);

    if (periodName != null && periodName !== '' && startTime !== '-' && endTime !== '-') {
      return `${periodName} (${startTime} - ${endTime} น.)`;
    }

    return this.displayValue(periodName);
  }

  private getSubjectCode(request: MakeupTeachingRequestModel): string {
    return this.displayValue(
      request.subject?.code ??
      request.subject_schedule_group?.subject?.code ??
      request.subject_schedule_group?.code
    );
  }

  private getSubjectName(request: MakeupTeachingRequestModel): string {
    return this.displayValue(
      request.subject?.name ??
      request.subject_schedule_group?.subject?.name ??
      request.subject_schedule_group?.name
    );
  }

  private buildTimetableRows(blocks: ScheduleSubmissionsTableModel['blocks']): TimetableRow[] {
    const eventsByDay = this.toTimetableEvents(blocks).reduce<Record<number, TimetableEvent[]>>(
      (acc, event) => {
        (acc[event.dayOfWeek] ??= []).push(event);
        return acc;
      },
      {}
    );

    return TIMETABLE_DAYS.map((day) => ({
      ...day,
      cells: this.buildTimetableCells(eventsByDay[day.dayOfWeek] ?? []),
    }));
  }

  private toTimetableEvents(blocks: ScheduleSubmissionBlockModel[]): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>)[] {
    return blocks.flatMap((block) => {
      if (block.type === 'subject_schedule') {
        return this.toSubjectScheduleTimetableEvent(block);
      }

      return this.toProjectResearchTimetableEvents(block);
    });
  }

  private toSubjectScheduleTimetableEvent(
    block: SubjectScheduleBlockModel
  ): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>)[] {
    const event = this.createTimetableEvent({
      dayOfWeek: block.time.day_of_week,
      startTime: block.time.start_time,
      endTime: block.time.end_time,
      subjectLabel: this.formatSubjectLabel(block.subject.code, block.section),
      subjectName: this.formatSubjectTooltip(block.subject.name, block.subject.code, block.section),
      detailLabel: block.time.room_name ?? '',
    });

    return event == null ? [] : [event];
  }

  private toProjectResearchTimetableEvents(
    block: ProjectResearchSubjectBlockModel
  ): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>)[] {
    if (!block.show_in_timetable || block.display_area !== 'timetable') {
      return [];
    }

    return block.teaching_times.flatMap((time) => {
      const event = this.createTimetableEvent({
        dayOfWeek: time.day_of_week,
        startTime: time.start_time,
        endTime: time.end_time,
        subjectLabel: this.formatSubjectLabel(block.subject.code, block.section),
        subjectName: this.formatSubjectTooltip(block.subject.name, block.subject.code, block.section),
        detailLabel: '',
      });

      return event == null ? [] : [event];
    });
  }

  private createTimetableEvent(params: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    subjectLabel: string;
    subjectName: string;
    detailLabel: string;
  }): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>) | null {
    const startSlot = this.toTimetableSlot(params.startTime);
    const endSlot = this.toTimetableSlot(params.endTime);

    if (startSlot == null || endSlot == null || endSlot <= startSlot) {
      return null;
    }

    const clampedStartSlot = Math.max(0, startSlot);
    const clampedEndSlot = Math.min(TIMETABLE_TOTAL_SLOTS, endSlot);

    if (clampedEndSlot <= clampedStartSlot) {
      return null;
    }

    return {
      dayOfWeek: params.dayOfWeek,
      startSlot: clampedStartSlot,
      endSlot: clampedEndSlot,
      subjectLabel: params.subjectLabel,
      subjectName: params.subjectName,
      detailLabel: params.detailLabel,
    };
  }

  private buildTimetableCells(events: TimetableEvent[]): TimetableCell[] {
    const sortedEvents = events
      .filter((event) => event.endSlot > 0 && event.startSlot < TIMETABLE_TOTAL_SLOTS)
      .sort((a, b) => a.startSlot - b.startSlot || a.endSlot - b.endSlot);
    const cells: TimetableCell[] = [];
    let slot = 0;

    while (slot < TIMETABLE_TOTAL_SLOTS) {
      let nextEvent: TimetableEvent | undefined;

      for (const event of sortedEvents) {
        if (event.startSlot >= slot) {
          nextEvent = event;
          break;
        }
      }

      if (nextEvent == null) {
        this.addEmptyTimetableCells(cells, slot, TIMETABLE_TOTAL_SLOTS);
        break;
      }

      if (nextEvent.startSlot > slot) {
        this.addEmptyTimetableCells(cells, slot, nextEvent.startSlot);
        slot = nextEvent.startSlot;
        continue;
      }

      const group = this.collectOverlappingEvents(sortedEvents, slot);
      cells.push({
        colSpan: group.endSlot - slot,
        events: group.events,
      });
      slot = group.endSlot;
    }

    return cells;
  }

  private addEmptyTimetableCells(cells: TimetableCell[], startSlot: number, endSlot: number): void {
    let currentSlot = startSlot;

    while (currentSlot < endSlot) {
      const nextHourSlot = Math.ceil((currentSlot + 1) / TIMETABLE_SLOTS_PER_HOUR) * TIMETABLE_SLOTS_PER_HOUR;
      const nextSlot = Math.min(nextHourSlot, endSlot);

      cells.push({ colSpan: nextSlot - currentSlot, events: [] });
      currentSlot = nextSlot;
    }
  }

  private collectOverlappingEvents(
    sortedEvents: TimetableEvent[],
    startSlot: number
  ): { endSlot: number; events: TimetableEvent[] } {
    let endSlot = startSlot;
    const events: TimetableEvent[] = [];
    let hasChange = true;

    while (hasChange) {
      hasChange = false;

      for (const event of sortedEvents) {
        const isOverlapping = event.startSlot < endSlot || (endSlot === startSlot && event.startSlot === startSlot);

        if (!isOverlapping || event.endSlot <= startSlot || events.includes(event)) {
          continue;
        }

        events.push(event);
        endSlot = Math.max(endSlot, event.endSlot);
        hasChange = true;
      }
    }

    return {
      endSlot: Math.min(endSlot, TIMETABLE_TOTAL_SLOTS),
      events,
    };
  }

  private toTimetableSlot(value: string): number | null {
    const match = /T(\d{2}):(\d{2})/.exec(value);

    if (match == null) {
      return null;
    }

    const hour = Number(match[1]);
    const minute = Number(match[2]);
    const minutesFromStart = ((hour - TIMETABLE_START_HOUR) * 60) + minute;

    return Math.floor(minutesFromStart / TIMETABLE_SLOT_MINUTES);
  }

  private formatSubjectLabel(code: string, section: string): string {
    return `${code} (${section})`;
  }

  private formatSubjectTooltip(subjectName: string | null | undefined, code: string, section: string): string {
    const normalizedSubjectName = String(subjectName ?? '').trim();

    return normalizedSubjectName === '' ? this.formatSubjectLabel(code, section) : normalizedSubjectName;
  }

  private formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear() + 543;

    return `${day}/${month}/${year}`;
  }

  private formatTimeRange(start?: string | null, end?: string | null): string {
    const startTime = this.formatTime(start);
    const endTime = this.formatTime(end);

    if (startTime === '-' || endTime === '-') {
      return '-';
    }

    return `${startTime} - ${endTime} น.`;
  }

  private formatTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const timeMatch = /T(\d{2}):(\d{2})/.exec(value);

    if (timeMatch != null) {
      return `${Number(timeMatch[1])}:${timeMatch[2]}`;
    }

    const timeParts = value.split(':');

    if (timeParts.length < 2) {
      return value;
    }

    const [hours, minutes] = timeParts;

    return `${Number(hours)}:${minutes}`;
  }

  private displayValue(value?: string | number | null): string {
    if (value == null || value === '') {
      return '-';
    }

    return String(value);
  }
}
